import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ArrowRight, Bed, Bath, Car, Download, CheckCircle } from 'lucide-react';
import { floorPlans } from '../data/mockData';

const FloorPlanDetail = () => {
  const { id } = useParams();
  const plan = floorPlans.find(p => p.id === id);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!plan) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[#262626] mb-4">Plan Not Found</h2>
          <Link to="/floor-plans">
            <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white">
              Back to Floor Plans
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % plan.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + plan.images.length) % plan.images.length);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Back Button */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/floor-plans" className="inline-flex items-center text-[#DAA520] hover:text-[#B8860B] transition-colors">
            <ArrowLeft size={20} className="mr-2" />
            Back to All Plans
          </Link>
        </div>
      </div>

      {/* Plan Header */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#262626] mb-2">{plan.name}</h1>
              <p className="text-xl text-gray-600">{plan.style}</p>
            </div>
            <div className="text-left md:text-right">
              <div className="text-4xl font-bold text-[#DAA520] mb-1">{plan.sqft}</div>
              <div className="text-gray-600">Square Feet</div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="relative h-96 md:h-[600px] overflow-hidden rounded-lg shadow-2xl">
              <img
                src={plan.images[currentImageIndex]}
                alt={`${plan.name} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              {plan.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-[#262626] rounded-full p-3 shadow-lg transition-all"
                  >
                    <ArrowLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-[#262626] rounded-full p-3 shadow-lg transition-all"
                  >
                    <ArrowRight size={24} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                    {currentImageIndex + 1} / {plan.images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Navigation */}
            {plan.images.length > 1 && (
              <div className="flex gap-4 mt-4 overflow-x-auto pb-2">
                {plan.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${
                      currentImageIndex === index ? 'border-[#DAA520] shadow-lg' : 'border-gray-200 hover:border-[#DAA520]/50'
                    }`}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Plan Details */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="border-2 border-[#DAA520]">
              <CardContent className="p-6 text-center">
                <Bed className="text-[#DAA520] mx-auto mb-3" size={40} />
                <div className="text-3xl font-bold text-[#262626] mb-1">{plan.bedrooms}</div>
                <div className="text-gray-600">Bedrooms</div>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#DAA520]">
              <CardContent className="p-6 text-center">
                <Bath className="text-[#DAA520] mx-auto mb-3" size={40} />
                <div className="text-3xl font-bold text-[#262626] mb-1">{plan.bathrooms}</div>
                <div className="text-gray-600">Bathrooms</div>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#DAA520]">
              <CardContent className="p-6 text-center">
                <Car className="text-[#DAA520] mx-auto mb-3" size={40} />
                <div className="text-3xl font-bold text-[#262626] mb-1">{plan.garage}</div>
                <div className="text-gray-600">Garage</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Description */}
            <div>
              <h2 className="text-3xl font-bold text-[#262626] mb-6">About This Plan</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {plan.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="flex-1">
                  <Button className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white py-6 text-lg">
                    Request Pricing
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Button variant="outline" className="flex-1 border-2 border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520] hover:text-white py-6 text-lg">
                  <Download size={20} className="mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-3xl font-bold text-[#262626] mb-6">Distinctive Features</h2>
              <ul className="space-y-4">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#DAA520] flex-shrink-0 mt-1" size={24} />
                    <span className="text-gray-700 leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Options */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#262626] mb-4">Customization Available</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This plan can be customized to meet your specific needs, lot requirements, and design preferences. Our team will work with you to create the perfect home.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {['Floor Plan Layout', 'Exterior Finishes', 'Interior Finishes', 'Structural Options'].map((option, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#DAA520]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="text-[#DAA520]" size={24} />
                  </div>
                  <h3 className="font-semibold text-[#262626]">{option}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Other Plans */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#262626] mb-8">Explore More Plans</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {floorPlans
              .filter(p => p.id !== plan.id)
              .slice(0, 3)
              .map((otherPlan) => (
                <Card key={otherPlan.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="relative overflow-hidden h-48">
                    <img
                      src={otherPlan.thumbnail}
                      alt={otherPlan.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#262626] mb-2">{otherPlan.name}</h3>
                    <p className="text-gray-600 mb-4">{otherPlan.sqft} sq. ft. • {otherPlan.style}</p>
                    <Link to={`/floor-plans/${otherPlan.id}`}>
                      <Button className="w-full bg-[#262626] hover:bg-[#DAA520] text-white">
                        View Details
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Build {plan.name}?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Schedule a consultation to discuss pricing, timeline, and customization options.
          </p>
          <Link to="/contact">
            <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
              Schedule Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FloorPlanDetail;
